import {UserProfile} from '@auth0/nextjs-auth0/client'
import {Session} from '@auth0/nextjs-auth0'
import {parse} from 'cookie'
import {IncomingMessage} from 'http'
import {once} from 'lodash'

import env from 'lib/config/public-env'
import {Pages} from 'lib/constants'

import LogRocket from './logrocket'

// Tell TypeScript that we attach the user to the window object
declare global {
  interface Window {
    __user?: CL.User
  }
}

// When auth is disabled, use a local user
export const localUser: CL.User = {
  accessGroup: 'local',
  adminTempAccessGroup: null,
  email: 'local',
  idToken: 'idToken'
}

export function isAdmin(user?: CL.User) {
  return user != null && user.accessGroup === env.ADMIN_ACCESS_GROUP
}

// Get a CL.User from a Session
export function userFromSession(
  req: IncomingMessage,
  session: Session
): CL.User {
  const user: CL.User = {
    ...session.user,
    // This is a namespace for a custom claim. Not a URL: https://auth0.com/docs/tokens/guides/create-namespaced-custom-claims
    accessGroup: session.user['http://conveyal/accessGroup'],
    adminTempAccessGroup: null,
    email: session.user.email ?? session.user.name,
    idToken: session.idToken
  }

  if (isAdmin(user)) {
    const adminTempAccessGroup = parse(
      req.headers.cookie || ''
    ).adminTempAccessGroup
    if (adminTempAccessGroup) user.adminTempAccessGroup = adminTempAccessGroup
  }

  return user
}

/**
 * Store the user on `window` for use globally.
 */
export function storeUser(user: CL.User): void {
  if (typeof window === undefined || env.AUTH_DISABLED) return

  // Add the adminTempAccessGroup for admins
  if (isAdmin(user)) {
    const adminTempAccessGroup = parse(
      document.cookie || ''
    ).adminTempAccessGroup
    if (adminTempAccessGroup) user.adminTempAccessGroup = adminTempAccessGroup
  }

  // Store the user on window, requiring a new session on each tab/page
  window.__user = user

  if (user.email && user.accessGroup) {
    // Identify the user for LogRocket
    LogRocket.identify(user.email, {
      accessGroup: user.accessGroup,
      email: user.email
    })
  }
}

/**
 * Log the user out by redirecting them to the logout page and log any associated errors.
 *
 * Wrap with `once` in case we encounter multiple errors simultaneously we only log it a single
 * time.
 */
export const logoutOnce = once((error?: Error, user?: UserProfile) => {
  if (error) {
    console.error(error)
    LogRocket.captureException(error, {
      tags: {
        accessGroup: String(user?.accessGroup),
        type: 'auth'
      }
    })
  }
  delete window.__user
  window.location.href = Pages.logout
})
